/* eslint-disable import/max-dependencies */
import React, { useEffect, useState, Suspense } from 'react';
import { NavItem } from 'src/components/nav';
import { useLocalizationContext, useTranslate } from '@im/react-localization';
import Drawer from '@mui/material/Drawer';
import { useMsal } from '@azure/msal-react';
import { Outlet, useNavigate } from 'react-router';
import { isEmbedded } from 'src/util';
import getCountryLanguages from 'src/util/getCountryLanguages';
import { Box } from '@mui/system';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import { useIsDown } from '@im/ui-lib/responsive';
import AlertProvider from '../../../provider/AlertProvider';
import { QueryForm } from 'src/components/QueryForm';
import { queryParams } from './navItems';
import { Option } from '@im/ui-lib/SelectOption';
import { Header, HEADER_HEIGHT } from 'src/components/HeaderX4C/Header';
import { useSearchParams, createSearchParams } from 'react-router-dom';
import { useUserRole } from 'src/provider/UserRoleProvider';
import { LeftSideNavWrapper } from 'src/components/LeftSideNav/LeftSideNavWrapper';
import SnackBarProvider from 'src/components/SnackBar/SnackBarProvider';
import { ErrorPage } from '@im/ui-lib/ErrorPage';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { ConfirmationDialogProvider } from 'src/components/ConfirmationDialog/ConfirmationDialog';
import SageAi from 'src/components/SageAi/SageAi';
import { IM_PRICING_GDE_UI_SAGECHAT_ENABLED } from 'src/util/constants';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlagEnabled';

const DEFAULT_DRAWER_WIDTH = 64;
const SESSION_LOCALE_KEY = 'GDE_locale';

const MainContent = (
  <AlertProvider>
    <SnackBarProvider>
      <ConfirmationDialogProvider>
        <Suspense
          fallback={
            <Box minHeight={300}>
              <CenterLoader />
            </Box>
          }
        >
          <Outlet />
        </Suspense>
      </ConfirmationDialogProvider>
    </SnackBarProvider>
  </AlertProvider>
);

export function HomeWrapper(prop: any) {
  const { instance } = useMsal();
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentUser = instance.getActiveAccount()?.name?.split(',');
  const firstName = currentUser?.[1].split(' ');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [proceedTo, setProceedTo] = useState<string>();
  const { locale, setLocale } = useLocalizationContext();
  const isSageChatEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_SAGECHAT_ENABLED
  );
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');

  const isTempDrawer = useIsDown('lg');
  useEffect(() => {
    setDrawerOpen(false);
  }, [isTempDrawer]);

  const avatarProps = {
    firstName: firstName?.[1] ?? '',
    lastName: currentUser?.[0] ?? '',
    navItems: [],
    onLogoutClick: () => instance.logout(),
    resellerID: '',
    username: instance.getActiveAccount()?.name ?? '',
  };

  const navigateAndKeepParams = (pathname: string, params = {}) => {
    navigate({
      pathname,
      search: createSearchParams({
        companyCode,
        countryCode,
        ...params,
      } as Record<string, string>).toString(),
    });
  };

  const onNavItemClick = (navItem: NavItem) => {
    if (navItem.route) {
      if (queryParams[navItem.route]) {
        setProceedTo(navItem.route);
      } else navigateAndKeepParams(navItem.route);
    }
    setDrawerOpen(false);
  };
  const userDetails = useUserRole();

  const listOfAccessableCountries = userDetails.listOfAccessableCountries;
  const countryOptions: Option[] = listOfAccessableCountries
    ? listOfAccessableCountries
    : [
        {
          label: 'United states - MD',
          value: 'US-MD',
        },
      ];

  const isUserHasAccess = listOfAccessableCountries.find(
    (country) => country.value === [countryCode, companyCode].join('-')
  )?.value;

  const getDefaultCountryCompany = () => {
    if (isUserHasAccess) {
      return isUserHasAccess;
    }
    if (
      !countryCode &&
      !companyCode &&
      userDetails.defaultCountryCompanyCode !== ''
    )
      return userDetails.defaultCountryCompanyCode;
    return '';
  };

  const [selectedCountry, setSelectedCountry] = useState<string>(
    getDefaultCountryCompany
  );
  const availableLanguages = getCountryLanguages(selectedCountry);

  const [defaultLanguage] = availableLanguages;

  const sessionLocale = sessionStorage.getItem(SESSION_LOCALE_KEY);

  useEffect(() => {
    if (selectedCountry !== '') {
      searchParams.set('countryCode', selectedCountry.split('-')[0]);
      searchParams.set('companyCode', selectedCountry.split('-')[1]);
      setSearchParams(searchParams, { replace: true });
      if (!isEmbedded()) {
        if (availableLanguages.includes(sessionLocale as string)) {
          setLocale(sessionLocale as string);
        } else {
          sessionStorage.setItem(SESSION_LOCALE_KEY, defaultLanguage);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, searchParams, setSearchParams]);

  const [reload, setReload] = useState<boolean>(false);

  useEffect(() => (reload ? window.location.reload() : undefined), [reload]);

  return (
    <>
      {proceedTo && (
        <QueryForm
          onClose={() => setProceedTo(undefined)}
          onProceed={(params: any) => {
            navigateAndKeepParams(proceedTo, params);
          }}
          queryParams={queryParams[proceedTo]}
        />
      )}
      {!isEmbedded() && (
        <Box>
          <Box
            data-testid="home_component_box"
            sx={(theme) => ({
              background: theme.palette.common.white,
              position: 'sticky',
              top: '0px',
              zIndex: 1000,
            })}
          >
            <Header
              AvatarProps={avatarProps}
              availableLanguages={availableLanguages}
              countryOptions={countryOptions}
              data-testid="gds-main-header"
              isAuthenticated
              onCountryChange={(e) => {
                setReload(true);
                setSelectedCountry(e.target.value as string);
              }}
              onLanguageChange={(e) => {
                sessionStorage.setItem(
                  SESSION_LOCALE_KEY,
                  e.target.value as string
                );
                setLocale(e.target.value as string);
              }}
              onMenuButtonClick={() => setDrawerOpen(!drawerOpen)}
              selectedCountry={selectedCountry}
              selectedLanguage={locale}
            />
          </Box>
          <Drawer
            ModalProps={{
              BackdropProps: {
                sx: { backgroundColor: 'transparent' },
              },
            }}
            aria-label="app navigation"
            data-testid="gds_home_component_drawer"
            onClose={() => setDrawerOpen(false)}
            open={isTempDrawer ? drawerOpen : true}
            sx={{
              '& .MuiDrawer-paper': {
                height: `calc(100% - ${HEADER_HEIGHT}px)`,
                top: HEADER_HEIGHT,
              },
            }}
            variant={isTempDrawer ? 'temporary' : 'persistent'}
          >
            <LeftSideNavWrapper
              data-testid="gds_home_component_sidenav"
              onNavItemClick={onNavItemClick}
            />
          </Drawer>
          <Box
            sx={{
              marginLeft: isTempDrawer ? '0px' : `${DEFAULT_DRAWER_WIDTH}px`,
              width: isTempDrawer
                ? '100%'
                : `calc(100% - ${DEFAULT_DRAWER_WIDTH}px)`,
            }}
          >
            {isUserHasAccess ? (
              MainContent
            ) : (
              <ErrorPage
                data-testid="gds-vp-access-denied-page"
                errorCode="403"
                errorText={translate('Common.CountryAccessDeniedDescription')}
                message={translate('Common.AccessDeniedError')}
              />
            )}
          </Box>
        </Box>
      )}
      {isEmbedded() && (
        <>
          {isUserHasAccess ? (
            MainContent
          ) : (
            <Stack alignItems={'center'}>
              <ErrorPage
                data-testid="gds-vp-access-denied-page"
                errorCode="403"
                errorText={translate('Common.CountryAccessDeniedDescription')}
                message={translate('Common.AccessDeniedError')}
              />
              <Button
                data-testid="gds-vp-error-page-back"
                onClick={() => navigate(-1)}
                startIcon={<NavigateBeforeIcon />}
                variant="newContained"
              >
                {translate('Common.BackToPrevious')}
              </Button>
            </Stack>
          )}
        </>
      )}
      {isSageChatEnabled && <SageAi />}
    </>
  );
}
